// ─────────────────────────────────────────────────────────────────────────────
// useDashboardData — owns every fetch behind the Dashboard and the shape the
// page renders from.
//
// The three sources are deliberately independent: the KPI metrics, the trend
// series behind the bandwidth chart, and the 7-day session history each fail on
// their own without blanking the other two. A failure leaves that slice `null`,
// which the page renders as its empty state — never as a zero.
//
// `refreshRef` comes from App.js: DaemonProvider parks a callback there and
// calls it after the backend reconnects, so the page can re-fetch without
// DaemonContext having to import the page (which would be circular).
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import {
  getDashboardMetrics,
  getDashboardTrends,
  getSessionHistory7Days,
  rangeToFromDate,
  buildDonutData,
  buildBandwidthData,
  mapAlerts,
  mapAdminActions,
} from "../../../services/dashboardService";

export const BANDWIDTH_RANGES = ["Last 24 Hours", "Last 7 Days", "Last 30 Days"];

export function useDashboardData(refreshRef) {
  const [metrics,        setMetrics]        = useState(null);
  const [trends,         setTrends]         = useState(null);
  const [sessionHistory, setSessionHistory] = useState(null);
  const [range,          setRange]          = useState(BANDWIDTH_RANGES[0]);
  const [loading,        setLoading]        = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      const data = await getDashboardMetrics();
      if (data) setMetrics(data);
    } catch (err) {
      console.warn("[Dashboard] metrics fetch failed:", err?.message);
    }
  }, []);

  const fetchTrends = useCallback(async (rangeLabel) => {
    try {
      const from = rangeToFromDate(rangeLabel);
      const data = await getDashboardTrends(from, new Date());
      if (data?.trends) setTrends(data.trends);
    } catch (err) {
      console.warn("[Dashboard] trends fetch failed:", err?.message);
    }
  }, []);

  const fetchSessionHistory = useCallback(async () => {
    try {
      const data = await getSessionHistory7Days();
      if (data) setSessionHistory(data);
    } catch (err) {
      console.warn("[Dashboard] session history fetch failed:", err?.message);
    }
  }, []);

  // Initial load — one pass over all three, `loading` clears when the slowest
  // settles. Deliberately not keyed on `range`; the effect below owns that.
  useEffect(() => {
    Promise.all([fetchMetrics(), fetchTrends(range), fetchSessionHistory()])
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Range selector — only the trend series depends on it.
  useEffect(() => { fetchTrends(range); }, [range, fetchTrends]);

  // Post-reconnect refresh hook for DaemonProvider (see the header note).
  useEffect(() => {
    if (!refreshRef) return undefined;
    refreshRef.current = () => {
      fetchMetrics();
      fetchTrends(range);
      fetchSessionHistory();
    };
    return () => { refreshRef.current = null; };
  }, [refreshRef, fetchMetrics, fetchTrends, fetchSessionHistory, range]);

  // ── Derived view data ──────────────────────────────────────────────────────
  const alerts = metrics ? mapAlerts(metrics.recent_alerts ?? []) : [];

  return {
    metrics,
    loading,
    range,
    setRange,
    donutData:     metrics ? buildDonutData(metrics.traffic_by_region ?? []) : [],
    alerts,
    criticalCount: alerts.filter((a) => a.sev === "critical").length,
    adminActions:  metrics ? mapAdminActions(metrics.recent_admin_actions ?? []) : [],
    sessionHistory,
    bandwidth:     trends ? buildBandwidthData(trends, range) : null,
  };
}
